// =============================================================================
// JoinPage — Invite landing page (/join/:id)
// =============================================================================
// Resolves invitation and shows the appropriate action based on status:
//   - NEW_USER       → "Create Account & Accept"
//   - EXISTING_USER  → "Sign In & Accept"
//   - ALREADY_MEMBER → "Already a member" notice
//   - Authenticated + email match   → "Accept Invitation"
//   - Authenticated + email mismatch → "Wrong account" warning
// =============================================================================

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useAuth } from '@/hooks/use-auth';
import { useAuthStore } from '@/lib/auth-store';
import { api } from '@/lib/api';
import { Button, Card, Spinner } from '@tkpod/ui';

type InviteStatus = 'NEW_USER' | 'EXISTING_USER' | 'ALREADY_MEMBER';

interface InviteResolveResponse {
  data: {
    email: string;
    roleSlug: string;
    organizationName: string;
    inviteToken: string;
    expiresAt: string;
    status: InviteStatus;
  };
}

export function JoinPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<InviteResolveResponse['data'] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const resolvedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!id || authLoading || resolvedIdRef.current === id) return;
    resolvedIdRef.current = id;

    async function resolve() {
      try {
        const res = await api.get<InviteResolveResponse>(
          `/api/v1/invitations/${id}/resolve`,
        );
        setInvite(res.data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to resolve invitation',
        );
      } finally {
        setLoading(false);
      }
    }

    void resolve();
  }, [id, authLoading]);

  const storeInviteAndNavigate = (path: string) => {
    if (!invite) return;
    sessionStorage.setItem('inviteToken', invite.inviteToken);
    sessionStorage.setItem(
      'inviteContext',
      JSON.stringify({
        email: invite.email,
        roleSlug: invite.roleSlug,
        organizationName: invite.organizationName,
      }),
    );
    if (id) {
      sessionStorage.setItem('pendingJoinId', id);
    }
    navigate(path, { replace: true });
  };

  const emailMatch =
    isAuthenticated && user?.email?.toLowerCase() === invite?.email?.toLowerCase();

  // --- Loading ---
  if (authLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-text-secondary">
            Resolving invitation…
          </p>
        </div>
      </div>
    );
  }

  // --- Error ---
  if (error || !invite) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
        <Card className="max-w-md w-full">
          <div className="p-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger/10">
              <svg className="h-6 w-6 text-danger" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-text-primary mb-2">
              Invalid Invitation
            </h2>
            <p className="text-sm text-text-secondary mb-6">
              {error ?? 'This invitation could not be found.'}
            </p>
            <Button variant="outline" onClick={() => navigate('/login', { replace: true })}>
              Go to Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // --- Already a member ---
  if (invite.status === 'ALREADY_MEMBER') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600">
              <span className="text-lg font-bold text-white">TK</span>
            </div>
          </div>
          <Card>
            <div className="p-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
                <svg className="h-6 w-6 text-success" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-text-primary mb-2">
                Already a Member
              </h2>
              <p className="text-sm text-text-secondary mb-6">
                You are already a member of <strong>{invite.organizationName}</strong>.
              </p>
              <Button
                className="w-full"
                onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login', { replace: true })}
              >
                {isAuthenticated ? 'Go to Dashboard' : 'Sign In'}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // --- Accept handler for authenticated users ---
  const handleAccept = async () => {
    setSubmitting(true);
    setAcceptError(null);
    try {
      const res = await api.post<{ user: import('@tkpod/shared').AuthUser }>(
        '/api/v1/auth/accept-invite',
        { inviteToken: invite.inviteToken },
      );
      if (res.user) {
        useAuthStore.getState().setUser(res.user);
      } else {
        await useAuthStore.getState().reinitialize();
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setAcceptError(
        err instanceof Error
          ? err.message
          : 'Failed to accept invitation.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // --- Invite resolved successfully ---
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600">
            <span className="text-lg font-bold text-white">TK</span>
          </div>
        </div>

        <Card>
          <div className="p-6 text-center">
            <h2 className="text-xl font-bold text-text-primary mb-2">
              You&apos;ve Been Invited
            </h2>
            <p className="text-sm text-text-secondary mb-1">
              {invite.organizationName
                ? `${invite.organizationName} has invited you to join their team`
                : 'You have been invited to join a team'}
            </p>
            <p className="text-sm text-text-secondary mb-6">
              as <strong className="capitalize">{invite.roleSlug}</strong>.
            </p>

            <div className="rounded-lg bg-surface-secondary px-4 py-3 mb-6 text-left">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-text-tertiary">Email:</span>
                <span className="font-medium text-text-primary">{invite.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <span className="text-text-tertiary">Role:</span>
                <span className="font-medium text-text-primary capitalize">{invite.roleSlug}</span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <span className="text-text-tertiary">Expires:</span>
                <span className="font-medium text-text-primary">
                  {new Date(invite.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            </div>

            {acceptError && (
              <div className="mb-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
                {acceptError}
              </div>
            )}

            {isAuthenticated && emailMatch ? (
              /* ---- Authenticated + correct email: direct accept ---- */
              <>
                <Button
                  className="w-full"
                  loading={submitting}
                  onClick={handleAccept}
                >
                  Accept Invitation
                </Button>
                <p className="mt-2 text-xs text-text-tertiary">
                  Signed in as <strong>{user?.email}</strong>
                </p>
              </>
            ) : isAuthenticated && !emailMatch ? (
              /* ---- Authenticated but wrong email ---- */
              <>
                <div className="mb-4 rounded-md bg-warning-light px-4 py-3 text-sm text-warning-dark text-left">
                  <p className="font-medium">Wrong account</p>
                  <p className="mt-1">
                    You are signed in as <strong>{user?.email}</strong>, but this invitation
                    was sent to <strong>{invite.email}</strong>.
                  </p>
                </div>
                <Button
                  className="w-full"
                  onClick={async () => {
                    await logout();
                    storeInviteAndNavigate('/login');
                  }}
                >
                  Sign Out &amp; Switch Account
                </Button>
              </>
            ) : invite.status === 'NEW_USER' ? (
              /* ---- New user: create account ---- */
              <>
                <Button
                  className="w-full"
                  onClick={() => storeInviteAndNavigate('/register')}
                >
                  Create Account &amp; Accept
                </Button>
                <p className="mt-3 text-xs text-text-tertiary">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => storeInviteAndNavigate('/login')}
                    className="text-brand-600 hover:text-brand-700 font-medium"
                  >
                    Sign In
                  </button>
                </p>
              </>
            ) : (
              /* ---- Existing user: sign in ---- */
              <>
                <Button
                  className="w-full"
                  onClick={() => storeInviteAndNavigate('/login')}
                >
                  Sign In &amp; Accept
                </Button>
                <p className="mt-3 text-xs text-text-tertiary">
                  This invite was sent to an existing account. Sign in to accept.
                </p>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
